/**
 * "What editors can change" — the list of configurable pieces of a block.
 *
 * A block is its own HTML. Somewhere inside it are the bits that should not be
 * fixed: the headline, the button's link, a colour. Each of those is one entry
 * here, and each entry answers exactly two questions in plain words — *what do
 * editors see it called* and *how does it behave* — with the rest (what it
 * starts as, whether it must be filled) right underneath.
 *
 * ### One artefact, one surface
 *
 * A configurable piece is two things at once: a `@param` line in the block's
 * `{# @component #}` comment and a `{{ … }}` in its markup. Editing them
 * separately is what made this hard — declaring `heading` put `{{ heading }}`
 * nowhere, renaming it broke every reference, and switching one to *Formatted
 * text* left Twig escaping its HTML. So every control here is a single
 * transform over the whole fragment (`manifestAuthoring.ts`): the declaration
 * and the markup always move together.
 *
 * The gesture that creates them lives next to the code, not here: highlight the
 * text and press *Make editable*. Nothing about the rendered email changes —
 * the highlighted text stays on as the Twig `|default(…)`.
 */
import { F, getIntlTranslation } from '@/utils/intl';
import {
  DeleteOutlined,
  PlusOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Select,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { humanizeParamName } from '../../components/EmailBodyEditor/visual/labels';
import {
  ComponentManifest,
  ManifestParam,
  ParamKind,
  parseManifest,
} from '../../components/EmailBodyEditor/visual/manifest';
import {
  FIELD_KIND_ORDER,
  findFieldReference,
  isFieldNameAvailable,
  isRawKind,
  readFieldFallback,
  sourceReadsField,
  withFieldAdded,
  withFieldFallback,
  withFieldRawness,
  withFieldRenamed,
} from '../../components/EmailBodyEditor/visual/manifestAuthoring';
import { inferManifest } from '../../components/EmailBodyEditor/visual/manifestInference';
import { replaceManifestComment } from '../../components/EmailBodyEditor/visual/manifestSerialize';
import { FIELD_TYPES } from './fieldTypes';

import styles from './EditableContentPanel.less';

/** A labelled line of the card — the whole card is four of these at most. */
const Line: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <label className={styles.line}>
    <span className={styles.lineLabel}>{label}</span>
    <span className={styles.lineControl}>{children}</span>
  </label>
);

/**
 * A text input that commits on blur rather than per keystroke.
 *
 * Every commit rewrites the whole buffer, and a name commit passing through
 * another field's name mid-typing would collide with it. `key` resets the draft
 * when the committed value changes underneath.
 */
const CommitInput: React.FC<{
  value: string;
  placeholder?: string;
  status?: 'error';
  onCommit: (value: string) => void;
}> = ({ value, placeholder, status, onCommit }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <Input
      size="small"
      value={draft}
      status={status}
      placeholder={placeholder}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => draft !== value && onCommit(draft)}
      onPressEnter={(event) => (event.target as HTMLInputElement).blur()}
    />
  );
};

interface EditableContentPanelProps {
  /** The fragment's draft content — the single source of truth. */
  content: string;
  fragmentKey: string;
  /** Receives the rewritten content (manifest comment and markup together). */
  onChange: (content: string) => void;
  /** Selects the field's `{{ … }}` in the code editor. */
  onReveal?: (from: number, to: number) => void;
}

const EditableContentPanel: React.FC<EditableContentPanelProps> = ({
  content,
  fragmentKey,
  onChange,
  onReveal,
}) => {
  const intl = useIntl();

  const manifest = useMemo(() => parseManifest(content), [content]);
  const inferred = useMemo(
    () => inferManifest(fragmentKey, content),
    [fragmentKey, content],
  );

  /**
   * The entries, declared or not. A block with no declaration shows what its
   * markup reads, and the first edit writes it down — there is no "declare
   * these" step to find. `type` is dropped: `fragment:<key>` is the registry's
   * fallback anyway, and writing it down would pin a generated name into the
   * block's contract.
   */
  const base: ComponentManifest = useMemo(
    () => manifest ?? { ...inferred, type: null },
    [manifest, inferred],
  );

  /**
   * Applies a markup edit and a declaration edit as one change. The markup goes
   * first: `replaceManifestComment` rewrites the comment over the result, so
   * doing it the other way round would work from stale offsets.
   */
  const commit = useCallback(
    (params: ManifestParam[], markup: (source: string) => string = (s) => s) =>
      onChange(replaceManifestComment(markup(content), { ...base, params })),
    [content, base, onChange],
  );

  const patch = useCallback(
    (
      name: string,
      changes: Partial<ManifestParam>,
      markup?: (source: string) => string,
    ) =>
      commit(
        base.params.map((param) =>
          param.name === name ? { ...param, ...changes } : param,
        ),
        markup,
      ),
    [base, commit],
  );

  /** Switching what a field *is* also switches how the markup reads it. */
  const changeKind = useCallback(
    (param: ManifestParam, kind: ParamKind) =>
      patch(param.name, { kind }, (source) =>
        withFieldRawness(source, param.name, isRawKind(kind)),
      ),
    [patch],
  );

  const changeFallback = useCallback(
    (param: ManifestParam, value: string) =>
      patch(param.name, { default: value || undefined }, (source) =>
        withFieldFallback(source, param.name, param.kind, value || undefined),
      ),
    [patch],
  );

  const rename = useCallback(
    (param: ManifestParam, next: string) =>
      onChange(
        withFieldRenamed(
          content,
          base,
          base.params.indexOf(param),
          next.trim(),
        ),
      ),
    [content, base, onChange],
  );

  const remove = useCallback(
    (param: ManifestParam) =>
      commit(base.params.filter((each) => each.name !== param.name)),
    [base, commit],
  );

  const add = useCallback(
    (kind: ParamKind) => onChange(withFieldAdded(content, base, kind)),
    [content, base, onChange],
  );

  /** Read by the markup, missing from the declaration — a hand-typed `{{ … }}`. */
  const undeclared = useMemo(() => {
    const declared = new Set(base.params.map((param) => param.name));
    return inferred.params.filter((param) => !declared.has(param.name));
  }, [base, inferred]);

  const addMenu = useMemo(
    () =>
      FIELD_KIND_ORDER.map((kind) => ({
        key: kind,
        label: (
          <span className={styles.addOption}>
            <span>{FIELD_TYPES[kind].label}</span>
            <span className={styles.addOptionHint}>
              {FIELD_TYPES[kind].description}
            </span>
          </span>
        ),
      })),
    [],
  );

  const kindOptions = useMemo(
    () =>
      FIELD_KIND_ORDER.map((kind) => ({
        value: kind,
        label: FIELD_TYPES[kind].label,
      })),
    [],
  );

  const renderCard = (param: ManifestParam) => {
    const reference = findFieldReference(content, param.name);
    const missing = !sourceReadsField(content, param.name);

    return (
      <div className={styles.card} key={param.name}>
        <Line
          label={
            <F
              id="pages.emailCenter.editable.seenAs"
              defaultMessage="Editors see"
            />
          }
        >
          <CommitInput
            value={param.labelKey ?? ''}
            placeholder={humanizeParamName(param.name)}
            onCommit={(value) => patch(param.name, { labelKey: value || null })}
          />
        </Line>

        <Line
          label={
            <F
              id="pages.emailCenter.editable.behavesAs"
              defaultMessage="Behaves as"
            />
          }
        >
          <Select
            size="small"
            className={styles.fullWidth}
            value={param.kind}
            options={kindOptions}
            onChange={(kind) => changeKind(param, kind)}
            optionRender={(option) => (
              <span className={styles.addOption}>
                <span>{FIELD_TYPES[option.value as ParamKind].label}</span>
                <span className={styles.addOptionHint}>
                  {FIELD_TYPES[option.value as ParamKind].description}
                </span>
              </span>
            )}
          />
        </Line>

        {param.kind === 'select' && (
          <Line
            label={
              <F
                id="pages.emailCenter.editable.choices"
                defaultMessage="Choices"
              />
            }
          >
            <Select
              size="small"
              className={styles.fullWidth}
              mode="tags"
              open={false}
              suffixIcon={null}
              value={param.options ?? []}
              placeholder={getIntlTranslation(intl, {
                id: 'pages.emailCenter.editable.choicesHint',
                defaultMessage: 'Type a choice and press Enter',
              })}
              onChange={(options: string[]) =>
                patch(param.name, {
                  options: options.length > 0 ? options : undefined,
                })
              }
            />
          </Line>
        )}

        <Line
          label={
            <F
              id="pages.emailCenter.editable.startsAs"
              defaultMessage="Starts as"
            />
          }
        >
          <CommitInput
            // The markup's own fallback when the declaration has none — a
            // hand-written block keeps its meaning rather than reading as unset.
            value={
              param.default ?? readFieldFallback(content, param.name) ?? ''
            }
            placeholder={getIntlTranslation(intl, {
              id: 'pages.emailCenter.editable.startsAsHint',
              defaultMessage: 'Nothing',
            })}
            onCommit={(value) => changeFallback(param, value)}
          />
        </Line>

        <div className={styles.cardFoot}>
          <Tooltip
            title={
              <F
                id="pages.emailCenter.editable.codeNameHint"
                defaultMessage="Its name in the code. Renaming updates every place that reads it."
              />
            }
          >
            <span className={styles.codeName}>
              <CommitInput
                value={param.name}
                status={
                  isFieldNameAvailable(
                    base,
                    base.params.indexOf(param),
                    param.name,
                  )
                    ? undefined
                    : 'error'
                }
                onCommit={(next) => rename(param, next)}
              />
            </span>
          </Tooltip>

          <Checkbox
            checked={param.required}
            onChange={(event) =>
              patch(param.name, { required: event.target.checked })
            }
          >
            <F
              id="pages.emailCenter.editable.required"
              defaultMessage="Must be filled in"
            />
          </Checkbox>

          <span className={styles.footActions}>
            {missing ? (
              <Tooltip
                title={
                  <F
                    id="pages.emailCenter.editable.missingHint"
                    defaultMessage="Nothing in the code uses this, so changing it would do nothing."
                  />
                }
              >
                <Tag color="warning">
                  <F
                    id="pages.emailCenter.editable.missing"
                    defaultMessage="not used"
                  />
                </Tag>
              </Tooltip>
            ) : (
              onReveal &&
              reference && (
                <Tooltip
                  title={
                    <F
                      id="pages.emailCenter.editable.show"
                      defaultMessage="Show it in the code"
                    />
                  }
                >
                  <Button
                    size="small"
                    type="text"
                    icon={<SelectOutlined />}
                    onClick={() => onReveal(reference.from, reference.to)}
                  />
                </Tooltip>
              )
            )}
            <Tooltip
              title={
                <F
                  id="pages.emailCenter.editable.remove"
                  defaultMessage="Make this fixed again"
                />
              }
            >
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(param)}
              />
            </Tooltip>
          </span>
        </div>
      </div>
    );
  };

  const addButton = (
    <Dropdown
      trigger={['click']}
      menu={{ items: addMenu, onClick: ({ key }) => add(key as ParamKind) }}
      classNames={{ root: styles.addMenu }}
    >
      <Button size="small" icon={<PlusOutlined />}>
        <F id="pages.emailCenter.editable.add" defaultMessage="Add one" />
      </Button>
    </Dropdown>
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Typography.Text strong>
          <F
            id="pages.emailCenter.editable.title"
            defaultMessage="What editors can change"
          />
        </Typography.Text>
        {addButton}
      </div>

      {base.params.length === 0 ? (
        <Typography.Paragraph type="secondary" className={styles.empty}>
          <F
            id="pages.emailCenter.editable.empty"
            defaultMessage="Nothing yet — everything in this block is fixed. Highlight a piece of the code above and press “Make editable”."
          />
        </Typography.Paragraph>
      ) : (
        <div className={styles.cards}>{base.params.map(renderCard)}</div>
      )}

      {undeclared.length > 0 && (
        <div className={styles.undeclared}>
          <Typography.Text type="secondary">
            <F
              id="pages.emailCenter.editable.undeclared"
              defaultMessage="Your code uses these, but they have no settings yet:"
            />
          </Typography.Text>
          {undeclared.map((param) => (
            <Tag
              key={param.name}
              className={styles.undeclaredTag}
              onClick={() => commit([...base.params, param])}
            >
              <PlusOutlined /> {param.name}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(EditableContentPanel);
